import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, Home } from 'lucide-react';
import PortfolioAuditorLogo from './FundWiseLogo';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);

  const faqData = [
    {
      category: "Getting Started",
      questions: [
        {
          question: `What is the ${process.env.REACT_APP_BETA_DURATION || '1-Month Free Trial'}?`,
          answer: (
            <div>
              <p>The {process.env.REACT_APP_BETA_DURATION || '1-month free trial'} gives you complete access to all Portfolio Auditor features at no cost. You can:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Analyze your portfolio</li>
                <li>Compare against benchmarks</li>
                <li>Explore all premium features</li>
              </ul>
              <p className="mt-2">No credit card required to start the trial.</p>
            </div>
          )
        },
        {
          question: "What are all prerequisite and how to setup this portfolio auditor tool on my PC?",
          answer: (
            <div>
              <p className="font-semibold mb-2">To setup Portfolio Auditor tool on your PC, follow these steps:</p>
              <ol className="list-decimal ml-5 space-y-2">
                <li><strong>Download your CAMS Mutual Fund investment statement</strong> - Refer to "How do I download Mutual Fund CAMS statement?" below</li>
                <li><strong>Install Microsoft Power BI Desktop</strong> - Download from <a href="https://www.microsoft.com/en-us/download/details.aspx?id=58494" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Microsoft's website</a></li>
                <li><strong>Get your license key</strong> - If you're a first-time user, place a Beta Free Trial order on our website to get a {process.env.REACT_APP_BETA_DURATION || '1-month free trial'} license key</li>
                <li><strong>Download the Portfolio Auditor tool</strong> - Available from the order page</li>
                <li><strong>Understand the downloaded package structure:</strong>
                  <ul className="list-disc ml-5 mt-2 space-y-1.5">
                    <li><strong>CAMS_uploader folder:</strong> Application for uploading CAMS statement. Stores transaction data in local SQLite DB in 'Database' folder as 'all_data' file. Activates license on first access.</li>
                    <li><strong>Benchmark_Fund folder:</strong> Contains 'MF_BenchmarkFund' file to specify benchmark funds. Choose scheme_code not in your current portfolio.</li>
                    <li><strong>NAV_Downloader folder:</strong> Downloads daily NAV data and updates DB. Run daily for latest data.</li>
                    <li><strong>Key folder:</strong> Maintains license key details post activation.</li>
                    <li><strong>Log folder:</strong> Tracks application runs and errors.</li>
                    <li><strong>PortfolioAuditor_InvestmentTracker_v1.0.pbix:</strong> Main dashboard - Open in Power BI Desktop and refresh to visualize data.</li>
                  </ul>
                </li>
                <li><strong>For subsequent days:</strong> Simply run NAV_Downloader application and refresh the Power BI report to view latest data.</li>
              </ol>
            </div>
          )
        },
        {
          question: "How do I download Mutual Fund CAMS statement?",
          answer: (
            <div>
              <p className="mb-2"><strong>Steps to download your CAMS statement:</strong></p>
              <ol className="list-decimal ml-5 space-y-2">
                <li>Visit <a href="https://www.camsonline.com/Investors/Statements/Consolidated-Account-Statement" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">CAMS Consolidated Account Statement</a></li>
                <li>Click <strong>'CAS - CAMS+KFintech'</strong> on left side navigation</li>
                <li>On right side, fill the form with:
                  <ul className="list-disc ml-5 mt-2 space-y-1.5">
                    <li><strong>Statement Type:</strong> Detailed (Includes transaction listing)</li>
                    <li><strong>Period:</strong> Specific Period</li>
                    <li><strong>From Date:</strong> Approximate date when you started your first investment</li>
                    <li><strong>To Date:</strong> Current date</li>
                    <li><strong>Folio Listing:</strong>
                      <ul className="list-circle ml-5 mt-1 space-y-1">
                        <li>'Without zero balance folios' - Current active funds only</li>
                        <li>'With zero balance folios' - Includes completely exited funds</li>
                      </ul>
                    </li>
                    <li><strong>Email:</strong> Email address linked to your mutual fund investments (repeat for multiple emails)</li>
                    <li><strong>PAN:</strong> Investment linked PAN number (mandatory despite showing optional - it's a CAMS site bug)</li>
                    <li><strong>Password & Confirm Password:</strong> Password to open the statement (same password needed when uploading in Portfolio Auditor tool)</li>
                  </ul>
                </li>
                <li>Click <strong>Submit</strong></li>
                <li>Check your email after 5-10 minutes for the CAMS statement. Verify all transactions are reflected properly.</li>
              </ol>
            </div>
          )
        },
        {
          question: "What are the system requirements?",
          answer: (
            <div>
              <p className="mb-2"><strong>Portfolio Auditor requires:</strong></p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Operating System:</strong> Windows 7 or higher</li>
                <li><strong>RAM:</strong> 4GB minimum (8GB recommended)</li>
                <li><strong>Disk Space:</strong> 500MB free space</li>
                <li><strong>Internet:</strong> Active connection for license verification and NAV data updates</li>
              </ul>
            </div>
          )
        },
        {
          question: `What happens after my ${process.env.REACT_APP_BETA_DURATION || '1-month trial'} ends?`,
          answer: (
            <div>
              <p>After {process.env.REACT_APP_BETA_PERIOD || '1 month'}, your trial license expires automatically.</p>
              <p className="mt-2">To continue using Portfolio Auditor PC desktop tool, purchase a license:</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li><strong>Monthly Plan:</strong> ₹{process.env.REACT_APP_INDIVIDUAL_PRICE || '99'}/month (1 PAN)</li>
                <li><strong>Family:</strong> ₹{process.env.REACT_APP_FAMILY_PRICE || '199'}/month (3 PANs)</li>
                <li><strong>Yearly Plan:</strong> ₹{process.env.REACT_APP_ANNUAL_PRICE || '699'}/year (3 PANs)</li>
              </ul>
              <p className="mt-2">Your data remains secure on your PC.</p>
            </div>
          )
        }
      ]
    },
    {
      category: "License Plans",
      questions: [
        {
          question: "What's the difference between Monthly Plan, Family, and Yearly Plan licenses?",
          answer: (
            <div>
              <p className="mb-3"><strong>All are PC desktop tool licenses:</strong></p>
              <ul className="space-y-2">
                <li><strong>Monthly Plan (₹{process.env.REACT_APP_INDIVIDUAL_PRICE || '99'}/month):</strong>
                  <ul className="list-disc ml-5 mt-1">
                    <li>PC desktop tool for 1 PAN card analysis</li>
                    <li>Perfect for solo investors</li>
                    <li>Monthly auto-renewal</li>
                  </ul>
                </li>
                <li><strong>Family (₹{process.env.REACT_APP_FAMILY_PRICE || '199'}/month):</strong>
                  <ul className="list-disc ml-5 mt-1">
                    <li>PC desktop tool for up to 3 PAN cards</li>
                    <li>Ideal for managing portfolios of multiple family members</li>
                    <li>Monthly auto-renewal</li>
                  </ul>
                </li>
                <li><strong>Yearly Plan (₹{process.env.REACT_APP_ANNUAL_PRICE || '699'}/year):</strong>
                  <ul className="list-disc ml-5 mt-1">
                    <li>PC desktop tool for up to 3 PAN cards</li>
                    <li>Annual subscription - Save ₹{(12 * parseInt(process.env.REACT_APP_INDIVIDUAL_PRICE || '99')) - parseInt(process.env.REACT_APP_ANNUAL_PRICE || '699')}/year</li>
                    <li>365 days validity</li>
                  </ul>
                </li>
              </ul>
              <p className="mt-3"><strong>All licenses include:</strong> Benchmark comparisons, daily tracking, tax calculations, and premium support on your PC.</p>
            </div>
          )
        },
        {
          question: "How does the license payment and renewal work?",
          answer: (
            <div>
              <p>We use <strong>Razorpay</strong> for secure automatic monthly license renewal payments.</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Your PC desktop tool license renews automatically each month</li>
                <li>Cancel anytime from your dashboard</li>
                <li>No penalties or hidden charges</li>
              </ul>
            </div>
          )
        },
        {
          question: "Can I upgrade or change my license plan?",
          answer: (
            <div>
              <p>Yes, you can upgrade or change your plan anytime from your dashboard.</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>Upgrade from Monthly Plan to Family or Yearly Plan</li>
                <li>Switch from monthly to annual billing</li>
                <li>Simply place a new order for the desired plan</li>
              </ul>
            </div>
          )
        },
        {
          question: "Is there a yearly subscription option?",
          answer: (
            <div>
              <p>Currently, we only offer monthly subscriptions to give you flexibility.</p>
              <p className="mt-2">We may introduce annual plans in the future based on user feedback.</p>
            </div>
          )
        }
      ]
    },
    {
      category: "License & Activation",
      questions: [
        {
          question: "How do I activate my license?",
          answer: (
            <div>
              <p className="mb-2"><strong>License activation steps:</strong></p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>After subscribing, you'll get a license key via email</li>
                <li>Download the Portfolio Auditor application to your Windows PC</li>
                <li>Open the application</li>
                <li>Enter the license key when prompted</li>
              </ol>
              <p className="mt-3">✓ Activation is instant and secured with RSA encryption</p>
            </div>
          )
        },
        {
          question: "Can I use one license on multiple computers?",
          answer: (
            <div>
              <p><strong>No.</strong> Each license is tied to specific PAN and computer.</p>
              <p className="mt-2"><strong>However:</strong> You can place a new order and download Portfolio Auditor on:</p>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>Different location on same PC, or</li>
                <li>Different PC</li>
              </ul>
            </div>
          )
        },
        {
          question: "What is PAN binding and why is it required?",
          answer: (
            <div>
              <p className="mb-2">PAN binding links your Portfolio Auditor license to your PAN card.</p>
              <p className="font-semibold mt-2">Why it's required:</p>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>Ensures secure access</li>
                <li>Prevents unauthorized usage</li>
                <li>Verifies you're the legitimate license holder</li>
              </ul>
              <p className="mt-3"><strong>PAN limits:</strong></p>
              <ul className="list-disc ml-5 mt-1">
                <li>Monthly Plan: 1 PAN</li>
                <li>Family: Up to 3 PANs</li>
                <li>Yearly Plan: Up to 3 PANs</li>
              </ul>
            </div>
          )
        },
        {
          question: "How do I re-verify my license?",
          answer: (
            <div>
              <p>License re-verification happens <strong>automatically</strong> when you open the application.</p>
              <ul className="list-disc ml-5 mt-2 space-y-1">
                <li>If your subscription is active, the app validates your license in the background</li>
                <li>If there's an issue, you'll be prompted to check your subscription status or re-enter your license key</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      category: "Features & Usage",
      questions: [
        {
          question: "Does my financial data leave my computer?",
          answer: (
            <div>
              <p className="font-semibold text-lg mb-2">No. 100% Privacy Guaranteed.</p>
              <p className="mb-2">Portfolio Auditor runs entirely on your Windows PC.</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>All portfolio data stays local</li>
                <li>CAMS statements remain on your PC</li>
                <li>Analysis data never uploaded</li>
                <li>We never store or access your financial information</li>
              </ul>
              <p className="mt-3"><strong>Only license verification</strong> connects to our servers.</p>
            </div>
          )
        },
        {
          question: "What makes Portfolio Auditor different from other tools?",
          answer: (
            <div>
              <p className="mb-2">Unlike typical portfolio trackers that show point-to-point returns:</p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-3 my-2">
                <p className="font-semibold">Portfolio Auditor simulates your exact investment and redemption transactions against benchmarks.</p>
              </div>
              <p className="mt-2"><strong>This 'apple-to-apple' comparison reveals:</strong></p>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>True outperformance or underperformance</li>
                <li>Daily precision analysis</li>
                <li>Not just end-to-end returns</li>
              </ul>
            </div>
          )
        },
        {
          question: "Can I compare my portfolio with custom benchmarks?",
          answer: (
            <div>
              <p className="font-semibold mb-2">Yes! Multiple benchmark options:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li><strong>Standard benchmarks:</strong> Nifty 50, Sensex</li>
                <li><strong>Custom mutual fund benchmarks:</strong> Any fund of your choice</li>
              </ul>
              <p className="mt-3">The tool replicates your investment timing and amounts to show how a benchmark would have performed with your exact transaction history.</p>
            </div>
          )
        },
        {
          question: "What analysis features are included?",
          answer: (
            <div>
              <p className="font-semibold mb-2">All plans include:</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Portfolio vs Benchmark comparison</li>
                <li>Daily portfolio tracking</li>
                <li>Investment summary</li>
                <li>Tax calculations</li>
                <li>Rolling return analysis</li>
                <li>NAV purchase analysis</li>
                <li>Outperformance/underperformance visualization</li>
                <li>Customizable dashboards</li>
                <li>Insights on underperforming funds</li>
              </ul>
            </div>
          )
        }
      ]
    },
    {
      category: "Billing & Cancellation",
      questions: [
        {
          question: "How do I cancel my license auto-renewal?",
          answer: (
            <div>
              <p className="mb-2"><strong>Easy cancellation process:</strong></p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Go to your dashboard</li>
                <li>Navigate to 'Subscriptions'</li>
                <li>Click 'Cancel Subscription'</li>
                <li>Confirm your choice</li>
              </ol>
              <p className="mt-3">✓ Your license will remain active until the current billing period ends</p>
              <p>✓ No cancellation fees apply</p>
            </div>
          )
        },
        {
          question: "Do you offer refunds?",
          answer: (
            <div>
              <p>Refunds are handled <strong>case-by-case</strong>.</p>
              <p className="mt-2">If you're unsatisfied within the first few days of purchasing a license:</p>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>Contact our support team</li>
                <li>We'll review your request</li>
                <li>Process refunds for eligible cases</li>
              </ul>
              <p className="mt-3"><em>Note: Trial licenses are always free, so no refunds apply there.</em></p>
            </div>
          )
        },
        {
          question: "What happens to my data after license cancellation?",
          answer: (
            <div>
              <p className="mb-2">Your data remains on your PC since Portfolio Auditor is a PC desktop tool that runs locally.</p>
              <p><strong>After license cancellation:</strong></p>
              <ul className="list-disc ml-5 mt-1 space-y-1">
                <li>✗ You won't be able to access the desktop tool features</li>
                <li>✓ Your local files stay intact on your PC</li>
                <li>✓ If you renew your license, everything works as before</li>
              </ul>
            </div>
          )
        },
        {
          question: "Will I be notified before automatic license renewal?",
          answer: (
            <div>
              <p><strong>Yes!</strong> You'll receive an email reminder a few days before your license auto-renews.</p>
              <p className="mt-2">This gives you time to cancel if you choose not to continue.</p>
            </div>
          )
        }
      ]
    },
    {
      category: "Technical & Support",
      questions: [
        {
          question: "How do I get support?",
          answer: (
            <div>
              <p className="mb-2"><strong>We offer two support channels:</strong></p>
              <ul className="list-disc ml-5 space-y-2">
                <li><strong>Live chat:</strong> Available on our website</li>
                <li><strong>Email support:</strong> support@portfolioauditor.com</li>
              </ul>
              <p className="mt-3">⏱️ Our team typically responds within 24 hours</p>
              <p>✓ All subscription plans include free support</p>
            </div>
          )
        },
        {
          question: "Are there future updates included?",
          answer: (
            <div>
              <p className="font-semibold text-lg mb-2">Yes! 100% Free Updates</p>
              <ul className="list-disc ml-5 space-y-1">
                <li>All subscribers receive free updates</li>
                <li>New features as we release them</li>
                <li>Continuous improvements based on user feedback</li>
              </ul>
            </div>
          )
        },
        {
          question: "What payment methods do you accept?",
          answer: (
            <div>
              <p className="mb-2">We accept all major payment methods through <strong>Razorpay:</strong></p>
              <ul className="list-disc ml-5 space-y-1">
                <li>Credit/Debit cards</li>
                <li>UPI</li>
                <li>Net banking</li>
                <li>Digital wallets</li>
              </ul>
              <p className="mt-3">🔒 All transactions are encrypted and PCI-DSS compliant</p>
            </div>
          )
        }
      ]
    }
  ];

  // Filter FAQ based on search term
  const filteredFAQ = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(
      item => {
        // Only search in question text since answer is JSX
        return item.question.toLowerCase().includes(searchTerm.toLowerCase());
      }
    )
  })).filter(category => category.questions.length > 0);

  const toggleAccordion = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-deep-space">
      {/* Header */}
      <nav className="bg-deep-space/80 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <PortfolioAuditorLogo size="sm" />
            </Link>
            <Link
              to="/"
              className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-outfit">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto font-ibm-plex">
            Find answers to common questions about Portfolio Auditor, subscriptions, and features.
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl border-2 border-white/10 focus:border-tech-blue focus:outline-none text-white placeholder-slate-400 transition-colors bg-white/5 backdrop-blur-xl shadow-sm"
            />
          </div>
        </div>

        {/* FAQ Accordion */}
        {filteredFAQ.length > 0 ? (
          <div className="space-y-8">
            {filteredFAQ.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                {/* Category Header */}
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center font-outfit">
                  <span className="w-2 h-8 bg-tech-blue rounded-full mr-3"></span>
                  {category.category}
                </h2>

                {/* Questions in this category */}
                <div className="space-y-3">
                  {category.questions.map((item, questionIndex) => {
                    const itemIndex = `${categoryIndex}-${questionIndex}`;
                    const isExpanded = expandedIndex === itemIndex;

                    return (
                      <div
                        key={questionIndex}
                        className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:bg-white/10"
                      >
                        {/* Question Header */}
                        <button
                          onClick={() => toggleAccordion(categoryIndex, questionIndex)}
                          className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                        >
                          <span className="font-semibold text-white pr-4 font-ibm-plex">
                            {item.question}
                          </span>
                          <div className="flex-shrink-0">
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5 text-tech-blue" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                          </div>
                        </button>

                        {/* Answer Content */}
                        <div
                          className={`overflow-hidden transition-all duration-300 ${
                            isExpanded ? 'max-h-[1000px]' : 'max-h-0'
                          }`}
                        >
                          <div className="px-6 py-4 bg-deep-space-surface/50 border-t border-white/10">
                            <div className="text-slate-300 leading-relaxed font-ibm-plex">
                              {item.answer}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          // No results found
          <div className="text-center py-12">
            <div className="text-slate-400 text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-bold text-white mb-2 font-outfit">No results found</h3>
            <p className="text-slate-400 font-ibm-plex">
              Try searching with different keywords or browse all questions above.
            </p>
          </div>
        )}

        {/* Contact Support Section */}
        <div className="mt-16 bg-gradient-to-r from-tech-blue to-blue-700 rounded-2xl p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-bold mb-3 font-outfit">Still have questions?</h3>
          <p className="text-blue-100 mb-6 max-w-xl mx-auto font-ibm-plex">
            Our support team is here to help. Reach out via live chat or email, and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.Tawk_API && window.Tawk_API.maximize()}
              className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg"
            >
              Start Live Chat
            </button>
            <a
              href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL || 'support@portfolioauditor.com'}`}
              className="bg-blue-800 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-900 transition-colors shadow-lg"
            >
              Email Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-deep-space border-t border-white/10 text-white py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-400 text-sm font-ibm-plex">
            © 2025 Portfolio Auditor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
